/**
 * Page copy for /industries/communications.
 *
 * Source: page-industry-communications.md, supplied by the client.
 *
 * Decisions carried over from the earlier industry pages:
 *
 * 1. METRICS. §6 tags AI Defect Intelligence as "a WeAreQA engagement", so the
 *    three figures ship as a WeAreQA client outcome. With this page the client
 *    has confirmed WeAreQA attribution for all five case studies, which is what
 *    retired the /industries hub's "Work our team has led" label.
 * 2. §4 NIS2. Built verbatim and flagged in CLAUDE.md. A targeted amendment was
 *    proposed in January 2026 and national laws will likely be revised again;
 *    the source asks for a quarterly re-check.
 * 3. SERVICE LINKS. The source's hrefs are earlier slugs; repointed at the
 *    routes that exist. /ai-maturity-model does not exist and is held at "#".
 *
 * NOT ACTIONED: the source's closing note asks for changes to
 * `01-IA-SITEMAP.md`. That file lives in design/archive-v1/ and is superseded —
 * see CLAUDE.md, "Which design files govern". It describes a different site.
 *
 * NO PLACEHOLDERS LEFT (2026-08-19). Client shape came from the deck, the price
 * slot was removed, and §4 was verified. See the notes at each.
 */

import type { IconName } from "../../components/ui/waq/Icon";

export const seo = {
  title: "Telecom & Communications Software Testing",
  description:
    "Quality engineering for operators and communications platforms: root cause analysis that closes, recurrence that stops, incident response under pressure.",
} as const;

export const hero = {
  breadcrumb: { label: "Industries", href: "/industries" },
  badge: "Communications",
  heading: "Quality Engineering for Communications",
  situation:
    "Your team fixed this defect eight months ago. Then again in the spring. It's back, in a different component, behaving the same way.",
  /** The turn: it is not a similar defect, it is the same one. */
  turn: "Not a similar defect — the same failure, wearing new clothes.",
  turnTail:
    " And nobody has time to work out why, because there are eleven other incidents open.",
  segmentsLabel: "Segments",
  segments: ["Operators / CSPs", "CPaaS and communications APIs"],
  groundLabel: "Regulatory ground",
  ground: ["NIS2"],
} as const;

/**
 * §2 — the page, and it comes straight out of the proof in §6. The 40%
 * repeat-defect figure is the unusual one: most QA case studies report volume or
 * speed. Building the page on recurrence means the thesis and the evidence are
 * the same thing rather than two arguments stapled together.
 */
export const recurrence = {
  heading: "Why do the same software defects keep recurring?",
  display: "Why the same defects keep coming back",
  stops: {
    lead: "Root cause analysis in communications almost never completes. It stops at ",
    emphasis: "which component failed",
    tail: " — because that's enough to restore service, and restoring service is the only thing anyone has capacity for.",
  },
  cause: {
    lead: "But ",
    emphasis: "which component",
    mid: " is not a cause. ",
    /** Three words, and the whole insight. */
    line: "It's a location.",
    tail: " The cause is whatever in the architecture, the interfaces, or the release process permits that class of failure to occur — and answering that takes a system-wide view no single engineer holds any more.",
  },
  loop: [
    "So the fix is local, the cause survives, and the defect reappears somewhere adjacent.",
    "Each recurrence gets logged as a new incident, so the pattern never becomes visible in the numbers. Your incident count looks like bad luck rather than a loop.",
  ],
  claim: {
    strong: "Making recurrence visible is the whole game.",
    tail: " Cluster defects by underlying cause rather than by component, and the loop shows up immediately — usually three or four causes producing most of what your team spends its year on.",
  },
} as const;

export type Item = { lead: string; tail: string; icon: IconName };

export const breaks = {
  heading: "What makes testing telecom and communications systems difficult?",
  display: "What actually breaks",
  items: [
    {
      lead: "Failures arrive at everyone at once.",
      tail: "There's no gradual rollout to a small cohort when the fault is in provisioning or routing. Blast radius is the default.",
      icon: "broadcast",
    },
    {
      lead: "Two stacks, one system.",
      tail: "A modern core alongside OSS and BSS older than most of the team, joined by integrations nobody fully documented.",
      icon: "layers",
    },
    {
      lead: "The test environment isn't the system.",
      tail: "Real network conditions, real carrier interconnects, real volume — none of it reproducible in staging, so a whole class of defect is only findable in production.",
      icon: "cube",
    },
    {
      lead: "For CPaaS, your API contract is someone's production dependency.",
      tail: "A change that's technically backward-compatible still breaks a customer who parsed your response the wrong way.",
      icon: "code",
    },
    {
      lead: "Delivery and quality vary by route.",
      tail: "Message and call quality depend on carriers and geographies you don't control, and your suite tests the happy path through one of them.",
      icon: "network",
    },
    {
      lead: "Severity is economic, not technical.",
      tail: "A cosmetic defect in a self-service portal and a service-affecting defect in provisioning get the same triage queue.",
      icon: "cost",
    },
  ] as Item[],
} as const;

/**
 * §4 — the sharpest CEO hook on the site. Every other page's business case is a
 * risk to the company; Article 20 personal liability is a risk to the reader.
 * The source says to state it plainly once and not labour it, so it is stated
 * once.
 *
 * "Where we stop" is essential here: NIS2 is a cybersecurity directive, and in
 * this sector the CISO is in the room.
 */
export const nis2 = {
  heading: "What does NIS2 require from telecom operators?",
  display: "NIS2 made this a board-level problem",
  status:
    "Electronic communications providers are essential entities under NIS2. Most EU Member States have now transposed it, national audit programmes are running, and the first fines have landed — Belgium, Italy, Hungary and Lithuania among them. Maximum exposure is €10 million or 2% of global turnover.",
  liability: {
    strong: "The part that changes the conversation isn't the fine.",
    tail: " Article 20 puts personal liability on management bodies, and national transpositions allow senior managers to be temporarily barred from their functions. This stopped being an IT budget line and became something a CEO carries personally.",
  },
  testableLead: "Two NIS2 obligations are testable, and that's where we work.",
  testable: [
    {
      body: "Incident reporting runs on a 24-hour early warning and 72-hour full notification. Most organisations have a documented procedure that has never been exercised under time pressure. Whether it actually completes in 24 hours is a scenario test, not a policy review — and the answer is usually no the first time you run it.",
      icon: "clock",
    },
    {
      body: 'The second is demonstrating that your risk-management measures work. Under proactive supervision, "we have a control" is a weaker answer than "here is evidence the control behaves as specified, tested on this date." Evidence generated continuously beats evidence assembled the week an auditor arrives.',
      icon: "fileText",
    },
  ] as { body: string; icon: IconName }[],
  limit: {
    label: "Where we stop.",
    tail: " NIS2 is a cybersecurity directive and we are not a cybersecurity firm. We do not do penetration testing, threat intelligence, or security architecture. We test that controls behave as specified and that operational processes function under the conditions they'll face. Engage a security specialist for the rest — we'll say so on the first call.",
  },
  /**
   * STATUS VERIFIED 2026-08-19. The placeholder asked for exactly this check.
   * **Re-check stays quarterly — next due November 2026** — and the section
   * remains on CLAUDE.md's launch-blocking list.
   *
   * Every claim in `status` and `liability` held up:
   *
   *   - Transposition: 23 of 27 Member States have fully transposed. "Most"
   *     is right. On 8 July 2026 the Commission referred Ireland, Spain,
   *     France and the Netherlands to the CJEU for failing to notify full
   *     transposition — so "most" is doing real work in that sentence.
   *   - First fines: Belgium (€185k), Italy (€450k), Hungary (€78k),
   *     Lithuania (€52k). The four countries named are exactly right.
   *   - The €10M / 2% of global turnover ceiling for essential entities.
   *   - Article 20 personal liability and temporary bars on senior managers.
   *   - The 24-hour early warning and 72-hour full notification.
   *
   * THE JANUARY 2026 AMENDMENT DOES NOT UNDERCUT THIS SECTION, which is the
   * thing worth knowing. Published 20 January 2026 as part of the Digital
   * Omnibus, it introduces a single EU reporting portal for Article 23 and 30
   * notifications and routes supervisors' coordination through it — the aim is
   * to remove duplicate reporting. It changes HOW an incident is notified, not
   * whether the 24/72-hour clocks exist. The two obligations this page calls
   * testable are untouched. Current draft foresees a 12-month transposition
   * period after entry into force.
   *
   * NOT ADDED: the four CJEU referrals, and the amendment itself. Both are
   * moving, and naming them would date the section faster than the general
   * statement it makes now.
   */
} as const;

export const capabilities = {
  display: "What we do",
  items: [
    {
      lead: "Defect intelligence.",
      tail: "Clustering by underlying cause rather than component, so recurrence becomes visible and the three or four causes driving most of your year surface.",
      icon: "brain",
    },
    {
      lead: "Root cause analysis that closes.",
      tail: "Not just which component — what permits the class of failure, and what change stops it recurring.",
      icon: "search",
    },
    {
      lead: "Trend prediction.",
      tail: "Where the next cluster is forming, based on change patterns and defect history.",
      icon: "trendUp",
    },
    {
      lead: "Interface and contract testing.",
      tail: "For operators, the integration seams between old and new. For CPaaS, the API contracts your customers depend on more literally than they told you.",
      icon: "network",
    },
    {
      lead: "Incident process testing.",
      tail: "Exercising detection, escalation and notification against the clock, before a regulator sets the clock for you.",
      icon: "clock",
    },
  ] as Item[],
  deliveryLead: "Delivered through ",
  delivery: [
    { label: "AI-Driven Validation", href: "/services/ai-driven-validation", tail: ", " },
    {
      label: "Test Architecture",
      href: "/services/qa-architecture-engineering",
      tail: ", or ",
    },
    {
      label: "Intelligent Quality Operations",
      href: "/services/intelligent-quality-operations",
      tail: ".",
    },
  ],
} as const;

export const proof = {
  display: "Proof",
  name: "AI Defect Intelligence",
  attribution: "A WeAreQA engagement",
  /**
   * From `WeAreQA-AI-Driven.pdf` slide 20, where this study appears in the AI
   * in Action Portfolio as "06 AI DEFECT INTELLIGENCE — Telecommunications
   * Enterprise". Same source as the shapes on fintech, healthcare and
   * ecommerce.
   *
   * DELIBERATELY ABSENT: engineer count, operator-or-platform, and incident
   * volume. The placeholder asked for all three and its suggested wording —
   * "A European operator, ~600 engineers across OSS and BSS, several hundred
   * defects per quarter" — was labelled "Example:" because it is illustration.
   * Factual claims about a real engagement; nobody supplied the real ones; not
   * invented here.
   */
  shape: "A telecommunications enterprise.",
  narrative:
    "Root cause analysis needed significant manual investigation, and the same classes of defect kept returning across different components. We built AI-assisted defect clustering and trend analysis over their own defect history.",
  metrics: [
    { value: "55%", label: "Faster root cause identification" },
    { value: "40%", label: "Reduction in repeat defects" },
    { value: "35%", label: "Improvement in defect trend prediction" },
  ],
  /** The source singles out the middle figure: the one that compounds. */
  highlightIndex: 1,
  read: {
    strong: "The middle number is the one that compounds.",
    tail: " Faster root cause saves hours per incident. Fewer repeat defects removes the incident entirely — and removes every future recurrence of it. One is efficiency, the other is the loop breaking.",
  },
  /**
   * NO "Read the full case study" LINK. Removed at the client's request.
   *
   * It was href="#" — it pointed at one of the five case studies the client
   * rejected as wrong and deleted, and no replacement has landed. /case-studies
   * is not a substitute: that hub lists the three AI use-case studies, not this
   * one. Same removal as /industries/fintech.
   *
   * If a replacement study ever lands for this industry, restore the link
   * pointing at the real route.
   */
} as const;

export const businessCase = {
  display: "The business case",
  note: "For the conversation with your CEO or board.",
  columnLabel: "What it costs today",
  rows: [
    {
      label: "Recurrence",
      cost: "Paying repeatedly to solve the same problem, with no compounding return",
      icon: "infinity",
    },
    {
      label: "Engineering capacity",
      cost: "Senior people permanently in incident response instead of building",
      icon: "users",
    },
    {
      label: "Customer impact",
      cost: "Failures reach the whole base at once; churn follows visible recurrence",
      icon: "broadcast",
    },
    {
      label: "NIS2 exposure",
      cost: "Fines to €10M or 2% of turnover — and personal liability for management",
      icon: "alert",
    },
    {
      label: "Audit readiness",
      cost: "Proactive supervision means evidence on demand, not evidence when asked",
      icon: "fileText",
    },
  ] as { label: string; cost: string; icon: IconName }[],
  /** "The first row is the one that decides this." */
  highlightIndex: 0,
  close:
    "The first row is the one that decides this. Everything else is downstream of paying three times for a fix you already bought.",
} as const;

export const notFit = {
  display: "Where we're not the right fit",
  items: [
    {
      lead: "Prime QA vendor at Tier-1 carrier scale.",
      tail: " We're a small senior team. Inside a large operator we work on defined programmes — defect intelligence, a specific platform, a testing capability — not the whole estate. If you need a partner who can staff hundreds, that isn't us.",
    },
    {
      lead: "Network and RF testing.",
      tail: " Protocol conformance, radio, and physical-layer testing need specialist labs and equipment.",
    },
    {
      lead: "Security testing under NIS2.",
      tail: " Penetration testing and threat intelligence are a security firm's work, not ours.",
    },
  ],
} as const;

/**
 * §9. NO PRICE, BY DECISION — consistent with the four service pages and the
 * other industry pages.
 *
 * **WHAT IS LEFT IS A HEADING AND TWO BUTTONS.** No body copy; the placeholder
 * was the only thing between "Starting from" and the CTAs.
 *
 * CONSIDERED AND DECLINED, 2026-08-19: one line replacing `display` would have
 * made it the closing CTA it resembles. The client chose to leave the heading as
 * the source wrote it. Do not revisit without asking.
 *
 * Four of five industry pages are now in this state; only pharma still holds
 * the placeholder.
 */
export const pricing = {
  display: "Starting from",
} as const;

export const related = {
  display: "Related",
  items: [
    { label: "AI-Driven Validation", tail: "", href: "/services/ai-driven-validation" },
    {
      label: "Test Architecture & QA Platform Engineering",
      tail: "",
      href: "/services/qa-architecture-engineering",
    },
    {
      label: "Intelligent Quality Operations",
      tail: "",
      href: "/services/intelligent-quality-operations",
    },
    { label: "AI Maturity Model", tail: "", href: "/#assessment" },
  ],
} as const;
